'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, useAnimation, useTransform, useScroll } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChevronLeft, ChevronRight, Award } from 'lucide-react';
import Image from 'next/image';
import { useLanguage } from '@/app/context/language-context';

// Mock data for awards
const awards = [
  {
    id: 1,
    title: 'Best Real Estate Agency 2023',
    organization: 'Dubai Property Awards',
    image:
      'https://images.x-space.ae/assets/images/Company Awards Pic/1.webp?height=200&width=200',
    year: 'Q3 2021',
    description:
      'Recognized for excellence in property sales and customer satisfaction.',
  },
  {
    id: 2,
    title: 'Top Luxury Property Broker',
    organization: 'Luxury Lifestyle Awards',
    image:
      'https://images.x-space.ae/assets/images/Company Awards Pic/2.webp?height=200&width=200',
    year: 2021,
    description:
      'Awarded for outstanding service in the luxury property segment.',
  },
  {
    id: 3,
    title: 'Innovation in Real Estate',
    organization: 'PropTech Awards',
    image:
      'https://images.x-space.ae/assets/images/Company Awards Pic/3.webp?height=200&width=200',
    year: 2022,
    description:
      'Recognized for innovative use of technology in real estate services.',
  },
  {
    id: 4,
    title: 'Customer Service Excellence',
    organization: 'International Business Awards',
    image:
      'https://images.x-space.ae/assets/images/Company Awards Pic/4.webp?height=200&width=200',
    year: 2022,
    description:
      'Awarded for exceptional customer service and client satisfaction.',
  },
  {
    id: 5,
    title: 'Best Off-Plan Property Agency',
    organization: 'Middle East Real Estate Awards',
    image:
      'https://images.x-space.ae/assets/images/Company Awards Pic/5.webp?height=200&width=200',
    year: 2021,
    description:
      'Recognized for excellence in off-plan property sales and marketing.',
  },
];

export default function AwardsSection() {
  const { t } = useLanguage();
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const panoramaRef = useRef(null);
  const [width, setWidth] = useState(0);

  // For manual navigation
  const [manualScroll, setManualScroll] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // For scroll-based animations
  const { scrollXProgress } = useScroll({
    container: panoramaRef,
  });

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  // Calculate panorama width on mount and resize
  useEffect(() => {
    if (panoramaRef.current) {
      setWidth(
        panoramaRef.current.scrollWidth - panoramaRef.current.offsetWidth
      );
    }
  }, []);

  // Trigger animation when section comes into view
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  // Mouse drag handlers for panorama
  const handleMouseDown = (e) => {
    if (!panoramaRef.current) return;
    setManualScroll(true);
    setStartX(e.pageX - panoramaRef.current.offsetLeft);
    setScrollLeft(panoramaRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!manualScroll || !panoramaRef.current) return;
    e.preventDefault();
    const x = e.pageX - panoramaRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    panoramaRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setManualScroll(false);
  };

  // Scroll to specific award
  const scrollToAward = (index) => {
    if (panoramaRef.current) {
      const awardWidth = panoramaRef.current.scrollWidth / awards.length;
      panoramaRef.current.scrollTo({
        left: index * awardWidth,
        behavior: 'smooth',
      });
    }
  };

  // Calculate parallax effects based on scroll position
  const parallaxBackground = useTransform(
    scrollXProgress,
    [0, 1],
    ['0%', '-20%']
  );

  return (
    <motion.section
      ref={ref}
      className='py-16 px-4 bg-white dark:bg-gray-900 overflow-hidden'
      variants={sectionVariants}
      initial='hidden'
      animate={controls}
    >
      <div className='max-w-7xl mx-auto'>
        {/* Section Header */}
        <div className='text-center mb-12'>
          <motion.div
            className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-primarycolor/10 mb-4'
            variants={titleVariants}
          >
            <Award className='w-8 h-8 text-primarycolor' />
          </motion.div>
          <motion.h2
            className='text-3xl font-bold text-gray-900 dark:text-white mb-4'
            variants={titleVariants}
          >
            {t('awardsRecognition')}
          </motion.h2>
          <motion.p
            className='text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'
            variants={titleVariants}
          >
            {t('awardsDescription')}
          </motion.p>
        </div>

        {/* Panorama Background */}
        {/* <motion.div
          className='absolute left-0 right-0 h-[500px] -z-10 opacity-10'
          style={{
            backgroundImage: 'url(\'/placeholder.svg?height=500&width=1500\')',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            x: parallaxBackground,
          }}
        /> */}

        {/* Panorama Slider */}
        <div className='relative overflow-hidden'>
          <div
            ref={panoramaRef}
            className='flex overflow-x-auto hide-scrollbar snap-x snap-mandatory'
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: manualScroll ? 'grabbing' : 'grab' }}
          >
            {awards.map((award, index) => (
              <motion.div
                key={`${index}`}
                className='min-w-[300px] md:min-w-[400px] h-[400px] snap-center mx-4 first:ml-0 last:mr-0 mb-12 mt-10'
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className='bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg h-full flex flex-col transform transition-all duration-300 hover:shadow-xl hover:-translate-y-2'>
                  <div className='relative h-48 overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center p-6'>
                    <Image
                      src={award.image}
                      alt={award.title}
                      width={150} // Required
                      height={150} // Required
                      sizes='(max-width: 768px) 100px, 150px'
                      priority={index === 0}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      style={{ width: 'auto', height: '100%' }} // Maintain aspect ratio
                      className='object-contain max-h-32'
                      unoptimized
                    />
                  </div>
                  <div className='p-6 flex-grow flex flex-col'>
                    <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                      {award.title}
                    </h3>
                    <p className='text-primarycolor dark:text-secondarycolor font-medium mb-3'>
                      {award.organization}
                    </p>
                    <p className='text-gray-600 dark:text-gray-400 flex-grow'>
                      {award.description}
                    </p>

                    <div className='mt-4 pt-4 border-t border-gray-100 dark:border-gray-700'>
                      <span className='inline-flex items-center text-sm text-gray-500 dark:text-gray-400'>
                        <Award className='w-4 h-4 mr-1 text-primarycolor ml-1' />
                        {t('realEstateExcellence')}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Scroll indicators */}
          <div className='absolute left-0 top-1/2 -translate-y-1/2 hidden md:block'>
            <motion.button
              onClick={() => {
                if (panoramaRef.current) {
                  panoramaRef.current.scrollBy({
                    left: -300,
                    behavior: 'smooth',
                  });
                }
              }}
              className='p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors shadow-md'
              whileHover={{ scale: 1.1, x: 5 }}
              whileTap={{ scale: 0.9 }}
              aria-label='Previous award'
            >
              <ChevronLeft className='w-5 h-5' />
            </motion.button>
          </div>

          <div className='absolute right-0 top-1/2 -translate-y-1/2 hidden md:block'>
            <motion.button
              onClick={() => {
                if (panoramaRef.current) {
                  panoramaRef.current.scrollBy({
                    left: 300,
                    behavior: 'smooth',
                  });
                }
              }}
              className='p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors shadow-md'
              whileHover={{ scale: 1.1, x: -5 }}
              whileTap={{ scale: 0.9 }}
              aria-label='Next award'
            >
              <ChevronRight className='w-5 h-5' />
            </motion.button>
          </div>
        </div>

        {/* Panorama Slider Controls */}
        {/* <div className='flex justify-center mb-6 space-x-4'>
          {awards.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToAward(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                Math.floor(scrollXProgress.get() * awards.length) === index
                  ? 'bg-primarycolor'
                  : 'bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600'
              }`}
              aria-label={`Go to award ${index + 1}`}
            />
          ))}
        </div> */}

        {/* Scroll instruction */}
        <div className='text-center text-sm text-gray-500 dark:text-gray-400'>
          <motion.div
            animate={{ y: [0, 5, 0] }}
            transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1.5 }}
            className='inline-flex items-center'
          >
            <span className='mr-2 font-semibold text-lg'>
              {t('scrollToExplore')}
            </span>
            <ChevronRight className='w-4 h-4' />
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}
